import _slicedToArray from "/drone/src/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { useScrollObserver } from "./armor-system-I2X5OK2J.js";
import { useResizeObserver } from "./armor-system-XQM4BHOJ.js";
import { getWindow } from "./armor-system-4JVSZEDO.js";

// src/hooks/useVirtualization.ts
import { useEffect, useMemo, useState } from "react";
var MIN_ITEM_HEIGHT = 36;
var MIN_SEGMENT_SIZE = 15;
var MAX_SAMPLE_SIZE = 100;
var INITIAL_STATE = {
  topOffset: 0,
  bottomOffset: 0,
  rangeStart: 0,
  rangeEnd: MIN_SEGMENT_SIZE * 2 - 1
};
var win = getWindow();
var getListBox = function getListBox(ref) {
  if (!ref || !ref.current) {
    return null;
  }
  return ref.current.getBoundingClientRect();
};
var useItemHeight = function useItemHeight(enabled, data, ref, averageItemHeight, itemSelector) {
  var dataLength = data.length;
  var _useState = useState(MIN_ITEM_HEIGHT),
    _useState2 = _slicedToArray(_useState, 2),
    dynamicAverageItemHeight = _useState2[0],
    setDynamicAverageItemHeight = _useState2[1];
  useEffect(function () {
    var _a, _b;
    if (!enabled || averageItemHeight !== void 0 || !dataLength || !ref.current) {
      return;
    }
    var items = itemSelector ? (_a = ref.current) == null ? void 0 : _a.querySelectorAll(itemSelector) : (_b = ref.current) == null ? void 0 : _b.children;
    if (!items || !items.length) {
      return;
    }
    var height = 0;
    var count = 0;
    for (var i = 0; i < items.length; i += 1) {
      if (i > MAX_SAMPLE_SIZE) {
        break;
      }
      height += items[i].getBoundingClientRect().height;
      count += 1;
    }
    setDynamicAverageItemHeight(Math.ceil(height / count));
  }, [ref, averageItemHeight, enabled, dataLength, itemSelector]);
  return averageItemHeight !== void 0 ? averageItemHeight : dynamicAverageItemHeight;
};
var makeState = function makeState(enabled, ref, windowHeight, totalItemCount) {
  var itemHeight = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : MIN_ITEM_HEIGHT;
  if (!totalItemCount || !windowHeight || !enabled) {
    return INITIAL_STATE;
  }
  var bodyOffset = 0;
  if (ref) {
    var bodyBox = getListBox(ref);
    if (!bodyBox) {
      return INITIAL_STATE;
    }
    bodyOffset = bodyBox.y;
  }
  var segmentSize = Math.max(Math.ceil(windowHeight / itemHeight), MIN_SEGMENT_SIZE);
  var segmentHeight = segmentSize * itemHeight;
  var visibleSegment = bodyOffset >= 0 ? 0 : Math.floor((windowHeight - bodyOffset) / segmentHeight);
  var rangeStart = (visibleSegment - 1) * segmentSize;
  var rangeEnd = rangeStart + segmentSize * 3 - 1;
  if (rangeStart < 0) {
    rangeStart = 0;
  }
  var topOffset = rangeStart * itemHeight;
  if (topOffset < 0) {
    topOffset = 0;
  }
  var bottomOffset = (totalItemCount - rangeEnd) * itemHeight;
  return {
    topOffset: topOffset,
    bottomOffset: bottomOffset,
    rangeStart: rangeStart,
    rangeEnd: rangeEnd
  };
};
var useVirtualization = function useVirtualization(enabled, containerRef, data, _ref) {
  var averageItemHeight = _ref.averageItemHeight,
    itemSelector = _ref.itemSelector,
    parentContainerRef = _ref.parentContainerRef;
  var _useState3 = useState(win ? win.innerHeight : 0),
    _useState4 = _slicedToArray(_useState3, 2),
    windowHeight = _useState4[0],
    setWindowHeight = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    windowOffset = _useState6[0],
    setWindowOffset = _useState6[1];
  var _useState7 = useState(),
    _useState8 = _slicedToArray(_useState7, 2),
    state = _useState8[0],
    setState = _useState8[1];
  useResizeObserver(enabled, setWindowHeight, void 0, {
    enableInitialCall: true,
    containerRef: parentContainerRef,
    throttleDelay: 100
  });
  useScrollObserver(enabled, setWindowOffset, {
    enableInitialCall: true,
    containerRef: parentContainerRef,
    throttleDelay: 100
  });
  var itemHeight = useItemHeight(enabled, data, containerRef, averageItemHeight, itemSelector);
  useEffect(function () {
    if (!enabled) {
      return;
    }
    var newState = makeState(enabled, containerRef, windowHeight, data.length, itemHeight);
    if (newState) {
      setState(function (currentState) {
        if (currentState && newState.topOffset === currentState.topOffset && newState.bottomOffset === currentState.bottomOffset && newState.rangeStart === currentState.rangeStart && newState.rangeEnd === currentState.rangeEnd) {
          return currentState;
        }
        return newState;
      });
    }
  }, [enabled, data, containerRef, itemHeight, windowHeight, windowOffset]);
  var realState = state || makeState(enabled, null, windowHeight, data.length);
  var virtualData = useMemo(function () {
    return enabled ? data.slice(realState.rangeStart, realState.rangeEnd) : data;
  }, [enabled, data, realState]);
  return {
    data: virtualData,
    topOffset: realState.topOffset,
    bottomOffset: realState.bottomOffset,
    rangeStart: realState.rangeStart,
    rangeEnd: realState.rangeEnd
  };
};
export { useVirtualization };